// src/components/ErrorNotification/ErrorNotification.js
import React, { useEffect, useMemo } from "react";
import "./errornotification.css";

/**
 * Props:
 *  - message: string (required)
 *  - variant?: "error" | "warn" | "info" | "success"
 *  - onClose?: () => void
 *  - autoHideMs?: number (e.g. 4000)
 */
const ErrorNotification = ({
  message,
  variant = "error",
  onClose,
  autoHideMs,
}) => {
  const text = useMemo(() => String(message || "").trim(), [message]);

  useEffect(() => {
    // Always run the hook, but only schedule when conditions are met
    if (!text) return;
    if (!autoHideMs) return;
    if (typeof onClose !== "function") return;

    const t = setTimeout(() => onClose(), autoHideMs);
    return () => clearTimeout(t);
  }, [text, autoHideMs, onClose]);

  if (!text) return null;

  const role = variant === "error" ? "alert" : "status";

  return (
    <div
      className={`notice notice--${variant}`}
      role={role}
      aria-live={variant === "error" ? "assertive" : "polite"}
    >
      <div className="notice__content">
        <span className="notice__icon" aria-hidden="true">
          {variant === "error" && "⛔"}
          {variant === "warn" && "⚠️"}
          {variant === "info" && "ℹ️"}
          {variant === "success" && "✅"}
        </span>

        <p className="notice__text">{text}</p>
      </div>

      {typeof onClose === "function" && (
        <button
          type="button"
          className="notice__close"
          aria-label="Dismiss notification"
          onClick={onClose}
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default ErrorNotification;
